import { useState, useMemo } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, ExternalLink } from "lucide-react";
import { SCHOOL, NAV_LINKS } from "../lib/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const desktopLinks = useMemo(() => NAV_LINKS.filter((l) => l.to !== "/area-soci"), []);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-[#FBFBF9]/85 border-b border-[#A3133A]/15"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-4 flex items-center justify-between gap-3">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-3 shrink-0">
          <img
            src={SCHOOL.logo}
            alt="Trinity Musical School"
            className="h-12 w-12 object-cover rounded-sm border border-[#A3133A]/15"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-serif-display text-[#A3133A] text-xl font-semibold">
              Trinity Musical School
            </div>
            <div className="text-[10px] tracking-eyebrow text-[#5C5C5C]">
              Amici per la Musica APS
            </div>
          </div>
        </Link>

        <nav className="hidden 2xl:flex items-center gap-2 min-[1880px]:gap-3.5" data-testid="navbar-desktop-nav">
          {desktopLinks.map((l) =>
            l.children ? (
              <div key={l.label} className="relative group" data-testid={`nav-group-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <button className="text-[11px] min-[1880px]:text-[12px] tracking-wide whitespace-nowrap transition-colors text-[#1C1C1C] hover:text-[#A3133A] inline-flex items-center gap-1">
                  {l.label} <ChevronDown size={12} className="transition-transform group-hover:rotate-180" />
                </button>
                <div className="absolute left-0 top-full pt-3 hidden group-hover:block z-50">
                  <div className="bg-[#FBFBF9] border border-[#A3133A]/15 shadow-xl min-w-[230px] py-2">
                    {l.children.map((c) =>
                      c.external ? (
                        <a
                          key={c.label}
                          href={c.href}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`nav-link-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className="flex items-center justify-between gap-3 px-4 py-2.5 text-[12px] tracking-wide text-[#1C1C1C] hover:bg-[#A3133A]/5 hover:text-[#A3133A] transition-colors"
                        >
                          {c.label} <ExternalLink size={11} className="opacity-50 shrink-0" />
                        </a>
                      ) : (
                        <Link
                          key={c.label}
                          to={c.to}
                          data-testid={`nav-link-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className="block px-4 py-2.5 text-[12px] tracking-wide text-[#1C1C1C] hover:bg-[#A3133A]/5 hover:text-[#A3133A] transition-colors"
                        >
                          {c.label}
                        </Link>
                      )
                    )}
                  </div>
                </div>
              </div>
            ) : l.external ? (
              <a
                key={l.label}
                href={l.href}
                target="_blank"
                rel="noreferrer"
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-[11px] min-[1880px]:text-[12px] tracking-wide whitespace-nowrap link-underline transition-colors text-[#1C1C1C] hover:text-[#A3133A]"
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-[11px] min-[1880px]:text-[12px] tracking-wide whitespace-nowrap link-underline transition-colors ${
                    isActive ? "text-[#A3133A]" : "text-[#1C1C1C] hover:text-[#A3133A]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden 2xl:flex items-center gap-2">
          <Link
            to="/area-soci"
            data-testid="nav-cta-soci"
            className="text-[11px] tracking-eyebrow text-[#A3133A] border border-[#A3133A]/40 px-3 py-2 rounded-sm hover:bg-[#A3133A] hover:text-white transition-all whitespace-nowrap"
          >
            Soci
          </Link>
          <Link
            to="/iscrizione"
            data-testid="nav-cta-iscriviti"
            className="text-[11px] tracking-eyebrow bg-[#A3133A] text-white px-4 py-2 rounded-sm hover:bg-[#8B0F2F] transition-all whitespace-nowrap"
          >
            Iscriviti
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Apri menu"
          onClick={() => setOpen(!open)}
          className="2xl:hidden p-2 text-[#A3133A]"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="2xl:hidden border-t border-[#A3133A]/15 bg-[#FBFBF9]"
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.flatMap((l) => (l.children ? l.children : [l])).map((l) =>
              l.external ? (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="py-3 px-2 text-sm tracking-wide border-b border-[#A3133A]/10 text-[#1C1C1C]"
                >
                  {l.label}
                </a>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `py-3 px-2 text-sm tracking-wide border-b border-[#A3133A]/10 ${
                      isActive ? "text-[#A3133A] font-medium" : "text-[#1C1C1C]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
}
